import { Card, Statistic, Select, Spin, Alert, Skeleton } from "antd";
import { ArrowUpOutlined, ArrowDownOutlined } from "@ant-design/icons";
import { Col, Row } from "antd";
import { useState, useEffect } from "react";
import useFeedbackService from "../../../../services/feedbackService";
import { useLanguage } from "../../../../context/LanguageContext";
import dashboardContent from "../../../../utils/dashboardContent";

const { Option } = Select;

const timeRanges = [
  { label: "Letzte 7 Tage", value: "7d" },
  { label: "Letzte 30 Tage", value: "30d" },
  { label: "Dieses Jahr", value: "year" },
];

// Funktion zur Berechnung der Veränderung
const getTrend = (current, previous, isPercentage = true) => {
  if (previous === 0) {
    return {
      icon: <ArrowUpOutlined />,
      color: "text-green-500",
      formattedChange: "+0%",
    };
  }

  const change = isPercentage
    ? ((current - previous) / previous) * 100
    : current - previous;

  const formattedChange = isPercentage
    ? `${change > 0 ? "+" : ""}${change.toFixed(1)}%`
    : `${change > 0 ? "+" : ""}${change.toFixed(1)}`;

  return {
    icon: change > 0 ? <ArrowUpOutlined /> : <ArrowDownOutlined />,
    color: change > 0 ? "text-green-500" : "text-red-500",
    formattedChange,
  };
};

const HeaderCards = () => {
  const { language } = useLanguage(); // für die Sprachwahl
  const text = dashboardContent[language].reports.analysis; // Holen der Texte für den Bereich "Reports"

  const [selectedRange, setSelectedRange] = useState("7d");
  const [metrics, setMetrics] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const { getOverviewMetrics } = useFeedbackService();

  useEffect(() => {
    const fetchMetrics = async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await getOverviewMetrics(
          selectedRange
        );
        if (response && response.metrics) {
          setMetrics(response.metrics);
        } else {
          setError(text.noDataAvailable); // Fehlertext aus dashboardContent
        }
      } catch (err) {
        console.error("Fehler beim Laden der KPIs:", err);
        setError(text.errorLoadingData); // Fehlertext aus dashboardContent
      }
      setLoading(false);
    };

    fetchMetrics();
  }, [selectedRange]);

  return (
    <div>
      {/* Zeitraum-Auswahl */}
      <div className="flex justify-end mb-4">
        <Select
          value={selectedRange}
          onChange={setSelectedRange}
          className="w-full md:w-48"
        >
          {timeRanges.map((range) => (
            <Option key={range.value} value={range.value}>
              {range.label}
            </Option>
          ))}
        </Select>
      </div>

      {/* Fehleranzeige */}
      {error && (
        <Alert message={text.error} description={error} type="error" showIcon />
      )}

      {/* Metriken-Karten */}
      <Row gutter={[16, 16]} className="mb-6">
        {[
          {
            title: text.totalFeedbacks,
            key: "allFeedbackCount",
            color: "#1890ff",
            isPercentage: true,
          },
          {
            title: text.positiveFeedbacks,
            key: "positiveFeedbackCount",
            color: "#52c41a",
            isPercentage: true,
          },
          {
            title: text.averageRating,
            key: "averageRating",
            color: "#faad14",
            suffix: "/ 5",
            isPercentage: false,
          },
          {
            title: text.activeForms,
            key: "formsCount",
            color: "#722ed1",
            isPercentage: false,
          },
        ].map((metric, index) => {
          const currentValue = metrics?.[metric.key]?.current;
          const previousValue = metrics?.[metric.key]?.previous;
          const trend =
            previousValue !== undefined
              ? getTrend(currentValue, previousValue, metric.isPercentage)
              : null;

          return (
            <Col key={index} xs={24} sm={12} md={6}>
              <Card
                bordered={false}
                className="shadow-md bg-white rounded-lg h-[9rem]"
              >
                {loading ? (
                  <Skeleton active paragraph={false} />
                ) : (
                  <>
                    <Statistic
                      title={metric.title}
                      value={currentValue !== undefined ? currentValue : "-"}
                      suffix={metric.suffix || ""}
                      valueStyle={{ color: metric.color }}
                    />
                    {trend && (
                      <div className={`flex items-center mt-2 ${trend.color}`}>
                        {trend.icon}
                        <span className="ml-2 text-sm">
                          {trend.formattedChange}
                        </span>
                      </div>
                    )}
                  </>
                )}
              </Card>
            </Col>
          );
        })}
      </Row>
    </div>
  );
};

export default HeaderCards;
